import { useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { motion } from "framer-motion";
import { motion as motionTokens, usePrefersReducedMotion } from "@tokens/index";

/**
 * Expand/collapse to a measured pixel height. The inner box is observed with
 * a ResizeObserver, so the outer box animates to a known number and never to
 * `height: auto`. Children mount with `open` and stay mounted until the
 * collapse has finished, then unmount so their local state resets.
 */
export function Collapse({
  open,
  children,
  testId,
}: {
  readonly open: boolean;
  readonly children: ReactNode;
  readonly testId?: string;
}): ReactElement | null {
  const reduced = usePrefersReducedMotion();
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  // `exiting` keeps the children mounted while the box closes. Derived in
  // render so the close and the first exit frame land in the same commit.
  // The previous `open` lives in state, not a ref: a ref mutated during
  // render is not idempotent, and StrictMode's second render pass then saw
  // no change, skipped `exiting`, and unmounted the children in one frame.
  const [exiting, setExiting] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open && !reduced) setExiting(true);
  }
  const mounted = open || exiting;

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (el === null || !mounted) return;
    setHeight(el.getBoundingClientRect().height);
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) setHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted]);

  if (!mounted) return null;

  return (
    <motion.div
      data-testid={testId}
      initial={false}
      animate={{ height: open ? height : 0, opacity: open ? 1 : 0 }}
      transition={{
        duration: reduced ? 0 : motionTokens.insertion.duration,
        ease: motionTokens.curves.decelerate,
      }}
      onAnimationComplete={() => {
        if (!open) setExiting(false);
      }}
      style={{ overflow: "hidden", flexShrink: 0 }}
      aria-hidden={open ? undefined : true}
    >
      <div ref={innerRef}>{children}</div>
    </motion.div>
  );
}
